// Fixture data for the pre-database UI.
//
// DERIVED FROM db/seed.json — the same file the real seed loads. That is
// deliberate: two hand-maintained copies of the pipeline would drift, and the
// moment they drift the fixtures start lying about what the database contains.
// One source, mapped into API shapes here.
//
// Used ONLY when the API is unreachable, with a visible badge saying so. Once
// the database is seeded this file can be deleted along with the fallback in
// App.tsx.

import seed from '../db/seed.json';
import type {
  CompanyCard, CompanyDetail, Contact, Touch, Activity, Decision, OpenQuestion, Settings,
} from './api.js';

type SeedCompany = (typeof seed.companies)[number] & Record<string, unknown>;

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);
const arr = (v: unknown): string[] => (Array.isArray(v) ? (v as string[]) : []);

const isPayra = (c: SeedCompany) => c.slug === 'payra';

// Counts that the live board computes in SQL have to be derived here instead.
function countsFor(c: SeedCompany) {
  if (!isPayra(c)) {
    return {
      contact_count: 0, contacts_with_email: 0, open_question_count: 0,
      sequence_status: null, touches_total: 0, touches_written: 0, touches_sent: 0,
    };
  }
  const p = seed.payra;
  return {
    contact_count: p.contacts.length,
    contacts_with_email: p.contacts.filter((x) => x.email).length,
    open_question_count: p.open_questions.length,
    sequence_status: p.sequence.status,
    touches_total: p.touches.length,
    touches_written: p.touches.filter((t) => t.body).length,
    touches_sent: p.touches.filter((t) => t.status === 'sent').length,
  };
}

function toCard(c: SeedCompany, i: number): CompanyCard {
  return {
    id: `seed-${i}`,
    slug: c.slug,
    name: c.name,
    stage: c.stage,
    priority: c.priority,
    fit_score: c.fit_score,
    fit_tier: c.fit_tier,
    timing: c.timing,
    gap_statement: str(c.gap_statement),
    next_action: str(c.next_action),
    conflict_gate: str(c.conflict_gate) ?? 'not_run',
    url: str((c as { url?: unknown }).url),
    lifecycle: 'target',
    ...countsFor(c),
  };
}

export const fixtureCompanies: CompanyCard[] = seed.companies.map((c, i) =>
  toCard(c as SeedCompany, i),
);

export function fixtureDetail(slug: string): CompanyDetail | null {
  const idx = seed.companies.findIndex((c) => c.slug === slug);
  if (idx < 0) return null;
  const c = seed.companies[idx] as SeedCompany;
  const card = toCard(c, idx);

  const contacts: Contact[] = isPayra(c)
    ? seed.payra.contacts.map((p, i) => ({
        id: `c-${i}`,
        name: p.name,
        title: p.title,
        role: p.role,
        entry_point: p.entry_point,
        email: p.email || null,
        linkedin: p.linkedin,
        source: p.source,
        notes: p.notes,
        suppressed: false,
      }))
    : [];

  const touches: Touch[] = isPayra(c)
    ? seed.payra.touches.map((t, i) => ({
        id: `t-${i}`,
        ref: t.ref,
        day_offset: t.day_offset,
        due_on: null,
        channel: t.channel,
        act: t.act,
        subject: t.subject,
        body: t.body,
        status: t.status,
        contact_name: t.contact_ref,
        notes: t.notes,
      }))
    : [];

  const activities: Activity[] = isPayra(c)
    ? seed.payra.activities.map((a, i) => ({
        id: `a-${i}`,
        kind: a.kind,
        occurred_at: a.occurred_at,
        summary: a.summary,
        detail: a.detail,
      }))
    : [];

  const decisions: Decision[] = isPayra(c)
    ? seed.payra.decisions.map((d, i) => ({
        id: `d-${i}`,
        decided_on: d.decided_on,
        decision: d.decision,
        decided_by: d.decided_by,
        reasoning: d.reasoning,
        rejected: d.rejected,
        status: d.status,
      }))
    : [];

  const open_questions: OpenQuestion[] = isPayra(c)
    ? seed.payra.open_questions.map((q, i) => ({
        id: `q-${i}`,
        question: q.question,
        blocking: q.blocking,
      }))
    : [];

  return {
    ...card,
    url: str(c.url), hq: str(c.hq), employees: num(c.employees), founded: num(c.founded),
    sub_category: str(c.sub_category), description: str(c.description),
    revenue_band: str(c.revenue_band), funding_stage: str(c.funding_stage),
    total_raised: str(c.total_raised), last_round_amount: str(c.last_round_amount),
    last_round_lead: str(c.last_round_lead), last_round_date: str(c.last_round_date),
    ownership_type: str(c.ownership_type), sponsor: str(c.sponsor), motion: str(c.motion),
    leadership_state: str(c.leadership_state), wedge: str(c.wedge),
    hamer_tier: str(c.hamer_tier), source: str(c.source),
    intent_why: str(c.intent_why), intent_win: str(c.intent_win),
    intent_framing: str(c.intent_framing), intent_origin: str(c.intent_origin),
    competitors: arr(c.competitors), end_markets: arr(c.end_markets),
    active_triggers: arr(c.active_triggers), hooks: arr(c.hooks),
    anecdotes: arr(c.anecdotes), investors: arr(c.investors),
    contacts, touches, activities, decisions, open_questions,
  };
}

export const fixtureSettings: Settings = {
  org: { id: 'seed', slug: seed.org.slug, name: seed.org.name },
  counts: {
    companies: seed.companies.length,
    contacts: seed.payra.contacts.length,
    sequences: 1,
  },
  members: [{ name: seed.user.name, email: seed.user.email, role: 'owner' }],
  connections: seed.connections.map((c, i) => ({
    id: `conn-${i}`,
    provider: c.provider,
    label: c.label,
    status: c.status,
    secret_ref: c.secret_ref ?? null,
    meta: (c as { meta?: { note?: string } }).meta ?? {},
    last_checked_at: null,
    last_error: c.status === 'error' ? 'not authorized' : null,
  })),
  identities: [
    {
      id: 'si-0',
      domain: seed.sending_identity.domain,
      from_name: seed.sending_identity.from_name,
      from_email: seed.sending_identity.from_email,
      status: seed.sending_identity.status,
      spf_ok: null,
      dkim_ok: null,
      dmarc_ok: null,
      daily_send_cap: seed.sending_identity.daily_send_cap,
      is_default: seed.sending_identity.is_default,
    },
  ],
};
